import { Injectable } from '@angular/core';
import { Router, NavigationStart, Event } from '@angular/router';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class UrlSanitizerService {
  private readonly unsafePattern = new RegExp(
    '%' +
    Array.from({ length: 32 }, (_, i) =>
      i.toString(16).padStart(2, '0')
    ).join('|%') +
    '|%7F',
    'gi'
  );

  constructor(private readonly router: Router) {
    this.router.events
      .pipe(
        filter(
          (event: Event): event is NavigationStart =>
            event instanceof NavigationStart
        )
      )
      .subscribe((event) => {
        if (!this.isUrlSafe(event.url)) {
          console.error('Unsafe URL detected:', event.url);
          this.router.navigate(['/']);
        }
      });
  }

  isUrlSafe(url: string): boolean {
    try {
      // Handle absolute URLs
      new URL(url);
      return true;
    } catch {
      // Handle relative URLs
      if (url.startsWith('/')) {
        return this.isValidRelativePath(url);
      }
      return false;
    }
  }

  private isValidRelativePath(path: string): boolean {
    const [cleanPath] = path.split(/[?#]/);
    const decodedPath = decodeURIComponent(cleanPath);

    return (
      !this.hasPathTraversal(decodedPath) &&
      !this.unsafePattern.test(decodedPath) &&
      this.isValidPathFormat(decodedPath)
    );
  }

  private isValidPathFormat(path: string): boolean {
    // Allow alphanumeric characters, hyphens, underscores, periods, spaces, and forward slashes
    // This covers standard URL paths while blocking potentially dangerous characters
    const validPathPattern = /^\/[a-zA-Z0-9\-_. /%]+$/;
    return validPathPattern.test(path);
  }

  private hasPathTraversal(path: string): boolean {
    return path.includes('..') || path.includes('./') || path.includes('//');
  }

  sanitizeUrl(url: string): string {
    try {
      const urlObject = new URL(url);
      if (this.isUrlSafe(url)) {
        return url;
      }

      const path = urlObject.pathname.replace(/\/+/g, '/').replace(/\.+/g, '.');

      return `${urlObject.protocol}//${urlObject.host}${path}${urlObject.search}${urlObject.hash}`;
    } catch {
      if (this.isUrlSafe(url)) {
        return url;
      }
      return url.replace(/\/+/g, '/').replace(/\.+/g, '.');
    }
  }
}
